"use client"

import React, {useMemo, useState} from 'react';
import { Image, Upload as AntUpload } from 'antd';
import {useSession} from "next-auth/react";
import {PlusOutlined} from "@ant-design/icons";
import {useErrorValidations, useResponseValidate} from "@/lib/hook";
import axiosInstance from "@/lib/api/axios";

function getBase64(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => resolve(reader.result);
        reader.onerror = error => reject(error);
    });
}

export const Upload = (
    {
        onFinishHandler,
        onRemovedHandler,
        label,
        url,
        maxCount,
        name = 'file',
        errorName = 'file',
        defaultFileList,
        accept= 'image/*',
        headers = {},
        uploadButton,
        listType = 'picture-card',
        isLoadingHandler,
        ...rest
    }) => {

    const dataSession = useSession()
    const {error, reset} = useErrorValidations(errorName)
    const {successValidate, errorValidate} = useResponseValidate()
    const [data, setData] = useState(()=>(
        {
            previewVisible: false,
            previewImage: '',
            previewTitle: '',
            fileList: defaultFileList
                ? defaultFileList
                : []
        }
    ))

    const handleCancel = () => {
        setData({...data, previewVisible: false})
    }

    const handlePreview = async (file) => {
        if (!file.url && !file.preview) {
            file.preview = await getBase64(file.originFileObj);
        }

        if (file?.type === 'application/pdf' || file?.name?.substring(file.name.lastIndexOf('.') + 1) === 'pdf'){
            window.open(file.url || file.response.url);
            return
        }

        setData({
            ...data,
            //previewFileType: file.type,
            previewImage: file.url || file.preview,
            previewVisible: true,
            previewTitle: file.name || file.url.substring(file.url.lastIndexOf('/') + 1),
        });
    };

    const handleChange = ({fileList, file}) => {
        reset()

        if (file){
            switch (file.status) {
                case 'done':
                    if (onFinishHandler){
                        onFinishHandler(file.response)
                    }
                    break
                case 'removed':
                    if (onRemovedHandler){
                        onRemovedHandler(file.response ?? file)
                    }
                    break
            }

        }
        setData({...data, fileList})
    }

    const uploadButtonElement = useMemo(()=>{
        if (uploadButton){
            return React.cloneElement(uploadButton, {listType})
        }

        return (
            <div>
                <PlusOutlined />
                <div style={{ marginTop: 8 }}>Upload</div>
            </div>
        )
    }, [uploadButton]);

    const uploadImage = async options => {
        const { onSuccess, onError, file } = options;

        if (url){
            const fmData = new FormData();
            fmData.append(name, file);
            if (isLoadingHandler){
                isLoadingHandler(true)
            }
            axiosInstance.post(
                url,
                fmData,
                {
                    headers: {
                        Authorization: `Bearer ${dataSession.data.accessToken}`,
                        ...headers
                    }
                }
            )
                .then((resp)=>{
                    successValidate(resp.data)
                    onSuccess(resp.data)
                })
                .catch((error)=>{
                    errorValidate(error)
                    onError({ error });
                })
                .finally(()=>{
                    if (isLoadingHandler){
                        isLoadingHandler(false)
                    }
                })
        }else {
            onSuccess(file)
        }
    };

    const showUploadButton = () =>{
        if (rest.fileList){
            return rest.fileList.length >= maxCount
        }

        return data.fileList.length >= maxCount
    }

    return(
        <>
            <AntUpload
                customRequest={uploadImage}
                accept={accept}
                listType={listType}
                fileList={data.fileList}
                onPreview={handlePreview}
                onChange={handleChange}
                maxCount={maxCount}
                {...rest}
            >
                {showUploadButton() ? null : uploadButtonElement}
            </AntUpload>

            <ImagePreview
                previewVisible={data.previewVisible}
                previewImage={data.previewImage}
                handleCancel={handleCancel}
            />
        </>
    )
};

const ImagePreview = ({previewVisible, previewImage, handleCancel}) =>{
    return(
        <div
            style={{
                display: 'none'
            }}
        >
            <Image
                preview={{
                    visible: previewVisible,
                    onVisibleChange: handleCancel,
                }}
                src={previewImage}
                height={130}
                width={130}
            />
        </div>
    )
}
